import type { RadioProps } from './Radio.types';
import type { PropsWithChildren } from 'react';
import { DotIcon } from 'icons/solid';
import clsx from 'clsx';

/**
 * The Radio component is simply a style wrapper for a simple radio button with
 * a traditional rounded button and a dot in the middle.
 *
 * It accepts children as the label for the radio button.
 *
 * At this point, this component should always be controlled, because it was
 * designed to be used with the HeadlessUI RadioGroup component.
 *
 * @link https://headlessui.com/react/radio-group
 */
export const Radio = (props: PropsWithChildren<RadioProps>) => {
  const {
    id = undefined,
    name = undefined,
    checked = false,
    value = undefined,
    disabled = false,
    children = null,
    error = false,
  } = props;

  return (
    <label
      className={clsx('flex gap-2 justify-start items-center', {
        'cursor-pointer': !disabled,
        'cursor-not-allowed': disabled,
      })}
    >
      <input
        type="hidden"
        id={id}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        readOnly
        data-testid="radio-input"
      />

      <div
        className={clsx(
          'w-5 h-5 rounded-full border flex-none flex justify-center items-center',
          !disabled && {
            'border-primary-pure-blue bg-primary-pure-blue': checked,
            'border-neutral bg-transparent': !checked,
            'border-feedback-error': error,
          },
          disabled && {
            'border-neutral bg-neutral-no-gray': !checked,
            'border-neutral bg-neutral': checked,
          },
        )}
      >
        {(checked || null) && (
          <div className="contents" data-testid="radio-dot">
            <DotIcon className="w-2 h-2 text-neutral-full-white" />
          </div>
        )}
      </div>

      <div className="flex flex-col justify-center select-none">{children}</div>
    </label>
  );
};

Radio.displayName = 'Radio';
